(() => {
  const root = document.querySelector('[data-noksha-shop-grid]');
  if (!root) return;

  const t = (key, fallback) => {
    const lang = document.documentElement.getAttribute('data-noksha-lang') || 'bn';
    return window.NokshaI18n?.[lang]?.[key] || fallback;
  };

  const searchInput = root.querySelector('[data-shop-search]');
  const searchForm = root.querySelector('[data-shop-search-form]');
  const sortSelect = root.querySelector('[data-shop-sort]');
  const list = root.querySelector('[data-shop-grid]');
  const empty = root.querySelector('[data-shop-empty]');
  const sentinel = root.querySelector('[data-shop-sentinel]');
  const status = root.querySelector('[data-shop-status]');
  const waitTextFallback = root.getAttribute('data-wait-text') || 'Please wait...';
  const errorTextFallback =
    root.getAttribute('data-error-text') || 'Could not update cart. Open the product and try again.';
  const loadMoreText = root.getAttribute('data-load-more-text') || 'Loading more dresses...';
  const sectionId = root.getAttribute('data-section-id') || '';
  let items = Array.from(root.querySelectorAll('[data-shop-item]'));
  let loadingMore = false;
  let observer = null;

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean)
      .filter((id, index, all) => all.indexOf(id) === index);

  const compareItems = (a, b, mode) => {
    const priceA = Number(a.dataset.price || 0);
    const priceB = Number(b.dataset.price || 0);
    const createdA = Number(a.dataset.created || 0);
    const createdB = Number(b.dataset.created || 0);
    const titleA = a.dataset.title || '';
    const titleB = b.dataset.title || '';
    const indexA = Number(a.dataset.index || 0);
    const indexB = Number(b.dataset.index || 0);

    switch (mode) {
      case 'price-ascending':
        return priceA - priceB;
      case 'price-descending':
        return priceB - priceA;
      case 'title-ascending':
        return titleA.localeCompare(titleB);
      case 'title-descending':
        return titleB.localeCompare(titleA);
      case 'created-ascending':
        return createdA - createdB;
      case 'created-descending':
        return createdB - createdA;
      default:
        return indexA - indexB;
    }
  };

  const applyFiltersAndSort = () => {
    const query = (searchInput?.value || '').trim().toLowerCase();
    const mode = sortSelect?.value || 'manual';
    let visible = 0;

    const ranked = items.slice().sort((a, b) => compareItems(a, b, mode));

    ranked.forEach((item) => {
      const haystack = item.getAttribute('data-search') || '';
      const matchesSearch = query === '' || haystack.includes(query);
      item.hidden = !matchesSearch;
      if (matchesSearch) visible += 1;
      if (list) list.appendChild(item);
    });

    if (empty) empty.hidden = visible > 0;
  };

  const notifyCartUpdated = (cart, variantId) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'noksha-shop-grid',
          data: {
            source: 'noksha-shop-grid',
            itemCount: cart?.item_count,
            variantId: String(variantId),
            sections: cart?.sections,
          },
        },
      })
    );
  };

  const addVariantToCart = async (variantId, { notify = true } = {}) => {
    const sections = cartSectionIds();
    const body = { id: variantId, quantity: 1 };
    if (sections.length) body.sections = sections.join(',');

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'Could not add to cart');
    }

    let cart = payload;
    if (typeof payload.item_count !== 'number') {
      cart = await (await fetch('/cart.js')).json();
      if (payload.sections) cart.sections = payload.sections;
    }

    if (notify) notifyCartUpdated(cart, variantId);
    return cart;
  };

  const bindBuyForm = (form) => {
    if (form.dataset.shopBuyBound === 'true') return;
    form.dataset.shopBuyBound = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      const errorNode = form.parentElement?.querySelector('[data-shop-buy-error]');
      const original = button?.innerHTML;
      const intent = form.getAttribute('data-shop-intent') || 'add';

      if (errorNode) {
        errorNode.hidden = true;
        errorNode.textContent = '';
      }
      if (button) {
        button.disabled = true;
        button.textContent = t('please_wait', waitTextFallback);
      }

      try {
        const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
        if (!variantId) throw new Error('Could not add to cart');

        await addVariantToCart(variantId, { notify: intent !== 'checkout' });

        if (intent === 'checkout') {
          window.location.href = '/checkout';
          return;
        }

        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) {
          drawer.open();
        }
      } catch (error) {
        if (errorNode) {
          errorNode.hidden = false;
          errorNode.textContent = t('buy_error', errorTextFallback);
        }
      } finally {
        if (button && original) {
          button.disabled = false;
          button.innerHTML = original;
        }
      }
    });
  };

  const nextPageUrl = (nextUrl) => {
    const current = new URL(window.location.href);
    const next = new URL(nextUrl, window.location.origin);
    const page = next.searchParams.get('page');
    if (page) current.searchParams.set('page', page);
    else current.searchParams.delete('page');
    if (sectionId) current.searchParams.set('section_id', sectionId);
    return current.toString();
  };

  const setNextUrl = (nextUrl) => {
    if (!sentinel) return;
    if (nextUrl) sentinel.setAttribute('data-next-url', nextUrl);
    else sentinel.removeAttribute('data-next-url');
  };

  const loadNextPage = async () => {
    if (!list || !sectionId || loadingMore) return;
    if (!sentinel?.getAttribute('data-next-url')) return;

    loadingMore = true;
    root.setAttribute('aria-busy', 'true');
    if (status) {
      status.hidden = false;
      status.textContent = loadMoreText;
    }

    try {
      // Keep fetching if a page is empty after hide filters (test tags / low price).
      while (sentinel.getAttribute('data-next-url')) {
        const nextUrl = sentinel.getAttribute('data-next-url');
        const response = await fetch(nextPageUrl(nextUrl), {
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Could not load more products');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(doc.querySelectorAll('[data-shop-item]'));
        incoming.forEach((item) => {
          const node = document.importNode(item, true);
          list.appendChild(node);
          items.push(node);
          node.querySelectorAll('[data-shop-buy-form]').forEach(bindBuyForm);
        });

        const following = doc.querySelector('[data-shop-sentinel]')?.getAttribute('data-next-url') || '';
        setNextUrl(following);
        applyFiltersAndSort();

        if (!following) {
          if (observer) observer.disconnect();
          break;
        }
        if (incoming.length > 0) break;
      }
    } catch {
      // Keep the next URL so a later scroll can retry.
    } finally {
      loadingMore = false;
      root.removeAttribute('aria-busy');
      if (status) status.hidden = true;
    }
  };

  observer =
    sentinel && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
          },
          { rootMargin: '800px 0px' }
        )
      : null;

  searchInput?.addEventListener('input', applyFiltersAndSort);
  sortSelect?.addEventListener('change', applyFiltersAndSort);

  searchForm?.addEventListener('submit', (event) => {
    const query = (searchInput?.value || '').trim();
    if (!query) {
      event.preventDefault();
      applyFiltersAndSort();
      searchInput?.focus();
      return;
    }

    const haystacks = items.map((item) => item.getAttribute('data-search') || '');
    const hasLocalMatch = haystacks.some((text) => text.includes(query.toLowerCase()));
    if (hasLocalMatch) {
      event.preventDefault();
      applyFiltersAndSort();
    }
  });

  root.querySelectorAll('[data-shop-buy-form]').forEach(bindBuyForm);

  if (observer && sentinel?.getAttribute('data-next-url')) {
    observer.observe(sentinel);
  }
})();
